/**
 * Runtime validation of untrusted ClientMessages (zod v4). Every bound here is a hard wall:
 * strings, arrays and numbers outside it reject the whole message. Unknown keys are stripped, so
 * nothing the schema does not name ever reaches the lobby or the engine. Free text that other
 * players will see is sanitised as part of parsing (see sanitize.ts).
 *
 * Game-rule validation (does the unit belong to you, is the city yours …) is the engine's job in
 * `GameSession.submit`; this layer only guarantees shape and sane magnitudes.
 */
import {
  MAX_FACTIONS,
  SCENARIO_IDS,
  type ScenarioId,
  SPECTATOR,
  TECHS,
  type TechId,
  THEATRE_IDS,
  type TheatreId,
  UNIT_TYPES,
  type UnitType,
} from '@friction/engine';
import { z } from 'zod';
import { LIMITS, normalizeRoomCode } from './limits';
import type { ClientMessage } from './messages';
import { sanitizeName, sanitizeText } from './sanitize';

const INT32_MAX = 0x7fffffff;

/** Raw strings may be longer than the sanitised limit (combining marks, NFKC expansion) — but not by much. */
const rawText = (limit: number) => z.string().max(limit * 4);

const techIds = Object.keys(TECHS) as [TechId, ...TechId[]];
const techId = z.enum(techIds);
const unitType = z.enum([...UNIT_TYPES] as [UnitType, ...UnitType[]]);
const speed = z.literal([1, 2, 3, 4, 5]);
const hexId = z.int().min(-1).max(LIMITS.maxHexId);
const unitId = z.int().min(0).max(INT32_MAX);
const tick = z.int().min(-1).max(LIMITS.maxTick);

export const commandSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('order'),
    unitIds: z
      .array(unitId)
      .min(1)
      .max(LIMITS.orderUnits)
      .refine((ids) => new Set(ids).size === ids.length, 'Duplicate unit ids'),
    kind: z.enum(['move', 'attack', 'hold', 'retreat', 'posture']),
    target: hexId,
    posture: z.enum(['aggressive', 'balanced', 'defensive']).optional(),
    executeAt: tick.optional(),
    channel: z.enum(['auto', 'courier', 'radio']).optional(),
    // Operation names reach ENEMY players through intercepts — sanitise like chat.
    opName: rawText(LIMITS.opName)
      .transform((s) => sanitizeText(s, LIMITS.opName) || undefined)
      .optional(),
  }),
  z.object({ type: z.literal('produce'), unitType, cityId: z.int().min(0).max(LIMITS.maxCityId) }),
  z.object({ type: z.literal('cancelProduction'), itemId: z.int().min(0).max(INT32_MAX) }),
  z.object({ type: z.literal('research'), tech: techId }),
  z.object({ type: z.literal('surrender') }),
]);

export const settingsPatchSchema = z.object({
  name: rawText(LIMITS.roomName)
    .transform((s) => sanitizeText(s, LIMITS.roomName))
    .optional(),
  mapSize: z.enum(['small', 'medium', 'large']).optional(),
  seed: z.int().min(0).max(0xffffffff).optional(),
  dayLimit: z.int().min(0).max(LIMITS.maxDayLimit).optional(),
  vpVictoryShare: z.number().min(0.5).max(1).optional(),
  speed: speed.optional(),
  startingTechs: z
    .array(techId)
    .max(techIds.length * 2)
    .transform((ids) => Array.from(new Set(ids)))
    .optional(),
  season: z.enum(['random', 'spring', 'summer', 'autumn', 'winter']).optional(),
  scenario: z
    .enum(SCENARIO_IDS as [ScenarioId, ...ScenarioId[]])
    .nullable()
    .optional(),
  theatre: z
    .enum(THEATRE_IDS as [TheatreId, ...TheatreId[]])
    .nullable()
    .optional(),
});

const slotIndex = z
  .int()
  .min(0)
  .max(LIMITS.slots - 1);

/** A faction id, or SPECTATOR (the whole truth). */
const perspective = z.union([slotIndex, z.literal(SPECTATOR)]);

export const clientMessageSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('hello'),
    version: z.int().min(0).max(INT32_MAX),
    name: rawText(LIMITS.playerName).transform(sanitizeName),
    token: z
      .string()
      .max(LIMITS.tokenLength)
      .regex(/^[A-Za-z0-9_-]*$/)
      .optional(),
  }),
  z.object({ t: z.literal('listRooms') }),
  z.object({ t: z.literal('createRoom'), settings: settingsPatchSchema }),
  z.object({ t: z.literal('joinRoom'), code: z.string().max(16).transform(normalizeRoomCode) }),
  z.object({ t: z.literal('leaveRoom') }),
  z.object({ t: z.literal('updateSettings'), settings: settingsPatchSchema }),
  z.object({
    t: z.literal('setSlot'),
    slot: slotIndex,
    controller: z.enum(['open', 'closed', 'human', 'ai']).optional(),
    aiDifficulty: z.enum(['recruit', 'veteran', 'general']).optional(),
    team: z
      .int()
      .min(0)
      .max(LIMITS.teams - 1)
      .optional(),
    color: z.int().min(0).max(0xffffff).optional(),
  }),
  z.object({ t: z.literal('takeSlot'), slot: slotIndex }),
  z.object({ t: z.literal('ready'), ready: z.boolean() }),
  z.object({ t: z.literal('startGame') }),
  z.object({
    t: z.literal('chat'),
    text: rawText(LIMITS.chatText).transform((s) => sanitizeText(s, LIMITS.chatText)),
  }),
  z.object({ t: z.literal('cmd'), seq: z.int().min(0).max(INT32_MAX), command: commandSchema }),
  z.object({ t: z.literal('setSpeed'), speed }),
  z.object({ t: z.literal('pause'), paused: z.boolean() }),
  z.object({ t: z.literal('spectate'), perspective }),
  z.object({ t: z.literal('ping'), time: z.number() }),
]);

/**
 * Validate an already-decoded value. Returns a sanitised ClientMessage, or null if the value is
 * not a well-formed, in-bounds message. Never throws.
 */
export function parseClientMessage(data: unknown): ClientMessage | null {
  const result = clientMessageSchema.safeParse(data);
  // The return statement doubles as a compile-time proof that the schema's output type is
  // assignable to the ClientMessage contract.
  return result.success ? result.data : null;
}

// ---- replay files (untrusted: they may come from anyone) ---------------------------------------

const factionIndex = z
  .int()
  .min(0)
  .max(MAX_FACTIONS - 1);
const color = z.int().min(0).max(0xffffff);
const season = z.enum(['spring', 'summer', 'autumn', 'winter']);
const replayTick = z.int().min(0).max(LIMITS.replayTicks);

/**
 * A match's SessionConfig as a replay file carries it: the known fields bounded as the lobby and
 * scenarios use them. Loose objects: config fields added by later engines (e.g. a real-world
 * theatre) are kept, not stripped — dropping one would replay a different war. What they may
 * hold is already bounded by the strict decoder (plain data, limited nesting and size).
 */
export const sessionConfigSchema = z.looseObject({
  seed: z.int().min(0).max(0xffffffff),
  mapSize: z.enum(['small', 'medium', 'large']),
  factions: z
    .array(
      z.looseObject({
        controller: z.enum(['human', 'ai']),
        aiDifficulty: z.enum(['recruit', 'veteran', 'general']).optional(),
        team: factionIndex.optional(),
        name: z.string().max(64).optional(),
        color: color.optional(),
        techs: z.array(techId).max(techIds.length).optional(),
        army: z.number().min(0.5).max(2).optional(),
        extraUnits: z
          .array(z.object({ type: unitType, count: z.int().min(0).max(20) }))
          .max(UNIT_TYPES.length)
          .optional(),
      }),
    )
    .min(2)
    .max(MAX_FACTIONS),
  dayLimit: z.int().min(0).max(LIMITS.maxDayLimit),
  vpVictoryShare: z.number().min(0.5).max(1),
  startingTechs: z.array(techId).max(techIds.length).optional(),
  season: season.optional(),
  scenario: z.string().max(32).optional(),
});

const recorded = z.array(z.tuple([factionIndex, commandSchema])).max(LIMITS.replayBatch);

/**
 * A ReplayLog (engine replay/log.ts). Shape and magnitudes only: the version is compared and
 * the ordering checked by `checkReplayLog`, and every command is re-validated by the engine when
 * the replay applies it — exactly as it was live.
 */
export const replayLogSchema = z.object({
  version: z.int(),
  config: sessionConfigSchema,
  entries: z
    .array(
      z.discriminatedUnion('k', [
        z.object({ k: z.literal('step'), t: replayTick, c: recorded }),
        z.object({ k: z.literal('flush'), t: replayTick, c: recorded }),
        z.object({ k: z.literal('ctrl'), t: replayTick, f: factionIndex, c: z.enum(['human', 'ai']) }),
      ]),
    )
    .max(LIMITS.replayEntries),
  endTick: replayTick,
  endHash: z.string().regex(/^[0-9a-f]{16}$/),
});

/** The small header of a replay file (what the film archive shows before a film is screened). */
export const replayFileHeaderSchema = z.object({
  replayVersion: z.int(),
  rules: z.string().max(32),
  theatre: z.string().max(128),
  factions: z
    .array(
      z.object({
        name: z.string().max(64),
        adjective: z.string().max(64),
        color,
        team: factionIndex,
        human: z.boolean(),
      }),
    )
    .min(2)
    .max(MAX_FACTIONS),
  winners: z.array(factionIndex).max(MAX_FACTIONS).nullable(),
  savedAt: z.string().max(40),
  endTick: replayTick,
  scenario: z.string().max(32).nullable(),
});
